/**
 * Lightweight runtime validation for the SSR-boundary backend responses.
 *
 * The full CompanyBasicResponse / CompanyNetworkResponse types are 50+
 * fields each — we don't validate every leaf. Instead, we assert the
 * contract that downstream client code depends on: the few fields whose
 * absence or wrong shape would have caused the "100% CPU hydration" mode
 * (May 2026 incident).
 *
 * On failure the SSR page logs the parse error with a small sample and
 * either 404s (basic response missing required identity) or drops the
 * graph (network response). It never passes through malformed payloads.
 */

import { z } from 'zod';
import type {
  CompanyBasicResponse,
  CompanyNetworkResponse,
  TopEmployerRank,
  TopEmployersResponse,
} from '@/lib/backend-api';
import { logger } from '@/lib/logger';

export const advancedSearchTotalsSchema = z.object({
  total: z.number().nonnegative(),
  total_capped: z.boolean().default(false),
  has_more: z.boolean().optional(),
});

// ── CompanyBasicResponse ──────────────────────────────────────────────────────

/** Fields the page MUST have to render at all. Everything else is best-effort. */
const companyBasicMinimalSchema = z
  .object({
    eu_id: z.string().min(1),
    display_name: z.string().min(1),
  })
  .passthrough();

/** Optional shape checks: arrays we iterate over must actually be arrays,
 *  not objects-pretending-to-be-arrays (a real backend bug we've seen). */
const companyBasicShapeSchema = z
  .object({
    nace_codes: z.array(z.unknown()).optional().nullable(),
    metrics: z.array(z.unknown()).optional().nullable(),
    tenders: z.array(z.unknown()).optional().nullable(),
    similar_companies: z.array(z.unknown()).optional().nullable(),
    guv_data: z.array(z.unknown()).optional().nullable(),
    employee_data: z.array(z.unknown()).optional().nullable(),
    subsidiaries: z.array(z.unknown()).optional().nullable(),
    lei_children: z.array(z.unknown()).optional().nullable(),
    participants: z.array(z.unknown()).optional().nullable(),
  })
  .passthrough();

/**
 * Validate an SSR `getCompanyBasic` response. Returns `null` (→ 404) when the
 * identity fields are missing, or the raw value when only optional arrays are
 * mis-shaped (logged but tolerated — the missing arrays just render empty).
 */
export function validateCompanyBasic(
  raw: unknown,
  context: { courtSlug: string; registerType: string; registerId: string },
): CompanyBasicResponse | null {
  if (raw === null || raw === undefined) return null;

  const identity = companyBasicMinimalSchema.safeParse(raw);
  if (!identity.success) {
    logger.error({
      message: 'SSR getCompanyBasic identity validation failed',
      file: 'lib/ssr-schemas.ts',
      ...context,
      issues: identity.error.issues.slice(0, 5),
    });
    return null;
  }

  const shape = companyBasicShapeSchema.safeParse(raw);
  if (!shape.success) {
    // Don't reject — just log. Downstream `?? []` fallbacks handle it.
    logger.warn({
      message: 'SSR getCompanyBasic array fields mis-shaped',
      file: 'lib/ssr-schemas.ts',
      ...context,
      issues: shape.error.issues.slice(0, 5),
    });
  }

  return raw as CompanyBasicResponse;
}

// ── CompanyNetworkResponse ───────────────────────────────────────────────────

/** Network graph contract: eu_id + nodes[] + edges[] are mandatory. */
const companyNetworkSchema = z
  .object({
    eu_id: z.string().min(1),
    nodes: z.array(
      z
        .object({
          id: z.string().min(1),
          // node_type isn't strictly enforced — we accept unknown new types,
          // a wrong node_type just falls through CompanyNetworkDiagram's
          // type switch and renders no node, which is harmless.
        })
        .passthrough(),
    ),
    edges: z.array(
      z
        .object({
          source: z.string().min(1),
          target: z.string().min(1),
        })
        .passthrough(),
    ),
  })
  .passthrough();

/**
 * Validate an SSR `getCompanyNetworkWithUpstream` response. Returns `null`
 * if the shape doesn't match — the page renders without the network diagram
 * rather than passing junk into ELK / xyflow.
 */
export function validateCompanyNetwork(
  raw: unknown,
  context: { euId: string },
): CompanyNetworkResponse | null {
  if (raw === null || raw === undefined) return null;

  const parsed = companyNetworkSchema.safeParse(raw);
  if (!parsed.success) {
    logger.error({
      message: 'SSR network response validation failed',
      file: 'lib/ssr-schemas.ts',
      ...context,
      issues: parsed.error.issues.slice(0, 5),
    });
    return null;
  }

  return raw as CompanyNetworkResponse;
}

/** Top-Arbeitgeber ranking contract (GET /public/top-lists/employers-by-salary). */
const topEmployersSchema = z.object({
  data: z.array(
    z
      .object({
        eu_id: z.string().min(1),
        name: z.string().min(1),
        register_court: z.string().nullable(),
        register_type: z.string().nullable(),
        register_id: z.string().nullable(),
        city: z.string().nullable(),
        bundesland: z.string().nullable(),
        nace_code: z.string().nullable(),
        nace_description: z.string().nullable(),
        logo_url: z.string().nullable(),
        favicon_url: z.string().nullable(),
        latest_employees: z.number(),
        latest_year: z.number(),
        salary_median_5y: z.number(),
        series: z.array(z.object({ year: z.number(), salary_per_employee: z.number(), employees: z.number() })),
      })
      .passthrough(),
  ),
  total: z.number().nonnegative(),
  methodology: z.object({
    years: z.number(),
    min_salary: z.number(),
    max_salary: z.number(),
    max_yoy_change: z.number(),
  }),
});

/**
 * Validate a Top-Arbeitgeber ranking response. Returns `null` when the shape
 * doesn't match, so the page renders its empty state instead of junk.
 */
export function validateTopEmployers(raw: unknown, context: { slug: string }): TopEmployersResponse | null {
  if (raw === null || raw === undefined) return null;
  const parsed = topEmployersSchema.safeParse(raw);
  if (!parsed.success) {
    logger.error({
      message: 'Top employers response validation failed',
      file: 'lib/ssr-schemas.ts',
      ...context,
      issues: parsed.error.issues.slice(0, 5),
    });
    return null;
  }
  return parsed.data as TopEmployersResponse;
}

/** Top-Arbeitgeber rank contract (GET /public/top-lists/employers-by-salary/rank). */
const topEmployerRankSchema = z.object({
  data: z
    .object({
      rank: z.number().int().positive(),
      total: z.number().int().positive(),
      min_employees: z.number().int().nonnegative(),
      max_employees: z.number().int().positive().nullable(),
      latest_employees: z.number(),
      latest_year: z.number(),
      salary_median_5y: z.number(),
    })
    .nullable(),
});

/** Validate a Top-Arbeitgeber rank response; `null` for an unranked company or a bad shape. */
export function validateTopEmployerRank(raw: unknown, context: { euId: string }): TopEmployerRank | null {
  if (raw === null || raw === undefined) return null;
  const parsed = topEmployerRankSchema.safeParse(raw);
  if (!parsed.success) {
    logger.error({
      message: 'Top employer rank response validation failed',
      file: 'lib/ssr-schemas.ts',
      ...context,
      issues: parsed.error.issues.slice(0, 5),
    });
    return null;
  }
  return parsed.data.data;
}
